import * as React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import EntityLink, { offerPath } from './EntityLink';

/** One entry of `offervariations/all-variations-map`, which is keyed by primary offer id. */
export type OfferVariation = {
  offerId: number;
  name: string;
  isActive: boolean;
  variationLogic?: string;
  flex?: number;
  step?: number;
};
export type VariationsMap = Record<string, OfferVariation[] | undefined>;

/** A primary offer's variations, or nothing for an offer that is not a primary. */
export const variationsOf = (
  offer: { id: number; isPrimaryVariant?: boolean | null },
  map: VariationsMap | undefined
) => (offer.isPrimaryVariant ? (map?.[offer.id] ?? null) : null);

/** What sets a variation's share of traffic: its weight in a FLEX group, its place in a STEP one. */
const describeVariation = (v: OfferVariation) =>
  v.variationLogic === 'FLEX'
    ? `ID ${v.offerId}, flex ${v.flex}`
    : v.variationLogic === 'STEP'
      ? `ID ${v.offerId}, step ${v.step}`
      : `ID ${v.offerId}`;

const chipSx = { height: 18, fontSize: '0.6875rem' };

/** The live/paused counts a row shows for a primary offer's variations. */
export function VariationChips({ variations }: { variations: OfferVariation[] | null | undefined }) {
  const live = variations?.filter((v) => v.isActive).length ?? 0;
  const paused = (variations?.length ?? 0) - live;
  return (
    <>
      {live > 0 && <Chip size='small' color='success' variant='outlined' label={`${live} live`} sx={chipSx} />}
      {paused > 0 && <Chip size='small' color='error' variant='outlined' label={`${paused} paused`} sx={chipSx} />}
    </>
  );
}

/** The variations themselves, for the popover: each with its status and a looking glass to its page. */
export function OfferVariationsList({ variations }: { variations: OfferVariation[] | null | undefined }) {
  if (!variations?.length) return null;
  const sorted = [...variations].sort((a, b) => a.offerId - b.offerId);
  return (
    <Box>
      <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
        Variations
      </Typography>
      <List
        dense
        disablePadding
        sx={{ border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}
      >
        {sorted.map((variation, index) => (
          <ListItem key={variation.offerId} divider={index < sorted.length - 1} sx={{ gap: 1, pl: 0.5 }}>
            <EntityLink to={offerPath(variation.offerId)} label={`Open offer ${variation.offerId} in a new tab`} />
            <ListItemText
              primary={variation.name}
              secondary={describeVariation(variation)}
              sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
            />
            <Chip
              size='small'
              label={variation.isActive ? 'Live' : 'Paused'}
              color={variation.isActive ? 'success' : 'error'}
              variant='outlined'
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
